import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { usePlayer } from "../context/PlayerContext";
import { Reveal } from "./Reveal";

// "If you liked this, listen next" — turns single-song traffic into catalog discovery.
export default function RelatedReleases({ current, title = "Keep the Night Going", overline = "If you liked this, listen next", limit = 4 }) {
  const [items, setItems] = useState([]);
  const { playRelease } = usePlayer();

  useEffect(() => {
    api.get("/releases").then((r) => {
      const all = (r.data || []).filter((x) => x.internalId !== current?.internalId);
      const sameType = all.filter((x) => x.releaseType === current?.releaseType);
      const rest = all.filter((x) => x.releaseType !== current?.releaseType);
      setItems([...sameType, ...rest].slice(0, limit));
    }).catch(() => {});
  }, [current?.internalId, current?.releaseType, limit]);

  if (items.length === 0) return null;
  const fire = (r, action) => track("related_song_click", { releaseId: r.internalId, metadata: { from: current?.internalId, action } });

  return (
    <section data-testid="related-releases" className="px-6 py-16 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">{overline}</p>
          <h2 className="mb-8 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">{title}</h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {items.map((r, i) => (
            <Reveal key={r.internalId} delay={i * 0.05}>
              <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-surface/60">
                <Link to={`/music/${r.slug}`} onClick={() => fire(r, "view")} data-testid={`related-${r.slug}`} className="block">
                  <div className="aspect-square overflow-hidden">
                    <img src={r.artwork} alt={`${r.title} — DJ Código cover art`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-semibold text-white">{r.title}</p>
                    <p className="text-[11px] uppercase tracking-widest text-white/40">{r.releaseType}{r.isNew ? " • New" : ""}</p>
                  </div>
                </Link>
                <button
                  aria-label={`Play ${r.title}`}
                  data-testid={`related-play-${r.slug}`}
                  onClick={() => { fire(r, "play"); playRelease(r); }}
                  className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-black shadow-lg transition-transform active:scale-90"
                >
                  <Play size={16} fill="currentColor" className="ml-0.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Link to="/#latest-releases" data-testid="explore-more-music" className="mt-8 inline-block text-xs font-semibold uppercase tracking-widest text-white/50 underline underline-offset-4 transition-colors hover:text-gold">
          Explore more music →
        </Link>
      </div>
    </section>
  );
}
